import { useAtom } from '@reatom/npm-react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { checkCurrencyReatom } from '@root/features/premium'
import { PremiumInfoDialog } from '@root/features/premium/premium-information/ui/desktop'
import { layoutHeightCss } from '@root/shared/ui-kit/utils/page-layout'

import { PremiumUnauthorizedCurrencyCheck } from './currency-check/currency-check'
import { PremiumUnauthorizedHeading } from './premium-unauthorized-heading'
import { PremiumHeroProducts } from './premium-unauthorized-products'

import type { ClassNameProps } from '@root/shared/react'

type PremiumUnauthorizedLayoutProps = {} & ClassNameProps

export const PremiumUnauthorizedContent: React.FC<
  PremiumUnauthorizedLayoutProps
> = ({ className }) => {
  const [subscriptionInfoDrawerOpen, setSubscriptionInfoDrawerOpen] =
    useState(false)
  const [boosterInfoDrawerOpen, setBoostersInfoDrawerOpen] = useState(false)

  const model = useMemo(
    () => checkCurrencyReatom(undefined, { name: 'premiumScreen' }),
    [],
  )
  const [result] = useAtom(model.checkCurrencyStateAtom)

  const isEmpty = result.value && AsyncStateN.isSuccess(result)

  return (
    <Root className={className}>
      <Centered>
        <GlowingStart />
        <GlowingEnd />
        <Group>
          <PremiumUnauthorizedHeading />
          <PremiumUnauthorizedCurrencyCheck model={model} />

          {isEmpty ? undefined : (
            <>
              <PremiumHeroProducts
                onBoostersInfoClick={() => setBoostersInfoDrawerOpen(true)}
                onSubscriptionInfoClick={() =>
                  setSubscriptionInfoDrawerOpen(true)
                }
              />
              <PremiumInfoDialog
                open={boosterInfoDrawerOpen}
                onOpenChange={setBoostersInfoDrawerOpen}
                product="boosters"
              />
              <PremiumInfoDialog
                open={subscriptionInfoDrawerOpen}
                onOpenChange={setSubscriptionInfoDrawerOpen}
                product="subscription"
              />
            </>
          )}
        </Group>
      </Centered>
    </Root>
  )
}

const Root = styled.div`
  ${layoutHeightCss}

  display: grid;
  grid-template-rows: minmax(0, 1fr);
  padding: 10px;
`

const Group = styled.div`
  position: relative;
  z-index: 0;
  display: grid;
  gap: 10px;
  margin-bottom: -100px;
`

const Centered = styled.div`
  border-radius: 10px;

  min-height: 0;
  min-width: 0;

  display: grid;
  justify-content: center;
  align-items: center;

  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    pointer-events: none;
    background: linear-gradient(
      to top,
      rgba(var(--appearance-inv-1-rgb), 0.1),
      rgba(var(--appearance-inv-1-rgb), 0)
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: -1;
    background: linear-gradient(
      to top,
      rgba(var(--appearance-inv-1-rgb), 0.05),
      rgba(var(--appearance-inv-1-rgb), 0)
    );
  }
`

const GlowingStart = styled.span`
  background-image: radial-gradient(
    50% 570px at 140px 100%,
    var(--subscriptionGradientFrom-4) 0%,
    transparent 100%
  );
  pointer-events: none;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.6;

  z-index: -1;

  &::before {
    content: '';

    opacity: 0.3;
    position: absolute;

    top: 20%;
    left: -5%;
    width: 90%;
    height: 130%;
    background-image: radial-gradient(
      40% 20% at 50% 70%,
      var(--subscriptionGradientFrom-9),
      transparent
    );

    transform: rotate(45deg);
  }
`

const GlowingEnd = styled.span`
  background-image: radial-gradient(
    30% 80% at 80% 100%,
    var(--subscriptionGradientTo-5) 0%,
    transparent 100%
  );
  pointer-events: none;

  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.8;

  z-index: -1;

  &::before {
    content: '';

    opacity: 0.45;
    position: absolute;
    top: 10%;
    left: 50%;
    width: 110%;
    height: 130%;

    background-image: radial-gradient(
      40% 20% at 50% 70%,
      var(--subscriptionGradientFrom-8),
      transparent
    );
    transform: rotate(45deg);
  }
`
